import React, { useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef, useLayoutEffect } from "react";
import "./AIChatControl.scss";
import { IconControl } from "spark-core-dx/components";
// Inject component-scoped CSS using Ryan's design tokens (from .app {...})
const __AI_CHAT_STYLE_ID = "ai-chat-control-styles";
function __ensureAIChatStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(__AI_CHAT_STYLE_ID)) return;
  const css = `ChatGPT-> Put your styles here and ill copy them into my file`; //you can leave it there for your testing app.
  if(!css.startsWith("ChatGPT")){
    const style = document.createElement("style");
    style.id = __AI_CHAT_STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
  }
}

/**
 * AIChatControl (React 18, JavaScript)
 * ------------------------------------
 * (…docstring unchanged…)
 */

const genId = () => "m_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 8);

// Fixed duplicate key & added Error role
const Role = {
  User: "user",
  Assistant: "assistant",
  System: "system",
  Tool: "tool",
  Error: "error",
};

const HISTORY_STORAGE_KEY = "AIChatControl.history";
const HISTORY_MAX = 200;

const Bubble = ({ id, role, children, onClick, title }) => {
  const isUser = role === Role.User;
  const base = "ai-bubble max-w-[85%] rounded-2xl px-4 py-2 shadow-sm whitespace-pre-wrap break-words";
  const userCls = "self-end ai-bubble-user";
  const asstCls = "self-start ai-bubble-assistant";
  const sysCls = "self-start ai-bubble-system";
  const toolCls = "self-start ai-bubble-tool";
  const errorCls = "self-start ai-bubble-error";
  const roleCls = role === Role.System ? sysCls : role === Role.Tool ? toolCls : role===Role.Error ? errorCls : isUser ? userCls : asstCls;

  return (
    <div onClick={onClick} className={`${base} ${roleCls} relative`} title={title}>
      {children}
    </div>
  );
};

const ThinkingBar = ({ aiChatId, onOpenDetails,thinking }) => (
  <div className="ai-thinkingbar">
    <span className="ai-spinner" />
    <span>{thinking ?? "Thinking…"}</span>
  </div>
);

const InputBar = ({
  value,
  onChange,
  onSubmit,
  disabled,
  submitDisabled,
  placeholder,
  onKeyDown,
  textareaRef,
  submitLabel = "Send"
}) => (
  <form onSubmit={onSubmit} className="w-full ai-inputbar">
    <div className="ai-chat-footer">
      <textarea
        ref={textareaRef}
        className="flex-1 ai-textarea"
        rows={1}
        placeholder={placeholder || "Type a message..."}
        value={value}
        disabled={disabled}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
      <button
        type="submit"
        disabled={submitDisabled || !value.trim()}
        className="ai-send disabled:opacity-50"
        aria-label={submitLabel}
        title={`${submitLabel} (Enter)`}
      >
        {submitLabel}
      </button>
    </div>
  </form>
);

function createStableApi({ getMessagesRef, setMessages, addMessage, updateMessage, appendToMessage, clear, focusInput, scrollToBottom, submitMessage }) {
  return {
    getMessages: () => getMessagesRef.current(),
    setMessages,
    addMessage,
    updateMessage,
    appendToMessage,
    clear,
    focusInput,
    scrollToBottom,
    submitMessage,
  };
}

const AIChatControlInner = (props, ref) => {
  const {
    initialMessages = [],
    placeholder = "Type a message...",
    disabled = false,
    onInit,
    onSend,
    onSave,
    footerContent = null,
    className = "",
    onOpenDetails,
    onOpenMessageDetails,
    getAssociatedOutputTarget,
    onOpenAssociatedOutput,
    scrollContent = null,
    reviewFooterContent = null,
    thinking,
    sendButtonLabel = "Send"
  } = props || {};

  const [messages, setMessagesState] = useState(() => {
    return (initialMessages || []).map(m => ({ id: m.id ?? genId(), createdAt: m.createdAt ?? Date.now(), ...m }));
  });
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [streamingId, setStreamingId] = useState(null);

  const listRef = useRef(null);
  const textareaRef = useRef(null);
  const saveTimerRef = useRef(null);
  const anchorRef = useRef(null);
  const submitMessageRef = useRef(async () => {});

  // === Input history (localStorage-backed) ===
  const historyRef = useRef([]);
  const histIndexRef = useRef(-1);          // -1 = not browsing; otherwise points into historyRef.current (0..n)
  const draftBeforeBrowseRef = useRef("");  // what user was typing before browsing

  // Load history on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (raw) {
        const list = JSON.parse(raw);
        if (Array.isArray(list)) historyRef.current = list.filter(s => typeof s === "string");
      }
    } catch (_) {}
  }, []);

  const saveHistory = () => {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(historyRef.current));
    } catch (_) {}
  };

  const pushHistory = (text) => {
    const t = (text || "").trim();
    if (!t) return;
    const arr = historyRef.current;
    // Deduplicate consecutive
    if (arr.length && arr[arr.length - 1] === t) return;
    arr.push(t);
    if (arr.length > HISTORY_MAX) arr.splice(0, arr.length - HISTORY_MAX);
    saveHistory();
  };

  const beginBrowseIfNeeded = () => {
    if (histIndexRef.current === -1) {
      draftBeforeBrowseRef.current = draft;
      histIndexRef.current = historyRef.current.length; // "after last"; ArrowUp shows latest
    }
  };

  const browseHistory = (delta) => {
    const n = historyRef.current.length;
    if (n === 0) return;
    beginBrowseIfNeeded();
    // clamp within [0, n] where n = return to original draft
    histIndexRef.current = Math.max(0, Math.min(n, histIndexRef.current + delta));
    if (histIndexRef.current === n) {
      setDraft(draftBeforeBrowseRef.current);
    } else {
      setDraft(historyRef.current[histIndexRef.current]);
    }
    // Move caret to end after update
    queueMicrotask(() => {
      if (textareaRef.current) {
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    });
  };

  const exitBrowseMode = () => {
    histIndexRef.current = -1;
  };

  // Keep latest messages accessible without re-creating the public API.
  const latestMessagesRef = useRef(messages);
  useEffect(() => { latestMessagesRef.current = messages; }, [messages]);

  const getMessagesRef = useRef(() => latestMessagesRef.current);

  const scrollToBottom = () => {
    if (anchorRef.current && typeof anchorRef.current.scrollIntoView === "function") {
      anchorRef.current.scrollIntoView({ block: "end" });
      return;
    }
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  };

  const focusInput = () => {
    if (textareaRef.current) textareaRef.current.focus();
  };

  const setMessages = (list) => {
    setMessagesState((Array.isArray(list) ? list : []).map(m => ({ id: m.id ?? genId(), createdAt: m.createdAt ?? Date.now(), ...m })));
  };

  const addMessage = (msg) => {
    const id = msg?.id || genId();
    setMessagesState(prev => [...prev, { id, createdAt: msg?.createdAt ?? Date.now(), role: msg?.role || Role.User, content: msg?.content || "" }]);
    return id;
  };

  const updateMessage = (id, updater) => {
    setMessagesState(prev => prev.map(m => {
      if (m.id !== id) return m;
      if (typeof updater === "function") {
        const next = updater(m) || m;
        return { ...m, ...next };
      }
      if (updater && typeof updater === "object") {
        return { ...m, ...updater };
      }
      return m;
    }));
  };

  const appendToMessage = (id, text) => {
    if (!text) return;
    setMessagesState(prev => prev.map(m => (m.id === id ? { ...m, content: (m.content || "") + text } : m)));
  };

  const clear = () => setMessagesState([]);

  // Debounced save hook when messages change
  useEffect(() => {
    if (!onSave) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try { onSave([...latestMessagesRef.current]); } catch (_) {}
    }, 350);
    return () => saveTimerRef.current && clearTimeout(saveTimerRef.current);
  }, [messages, onSave]);

  // Auto-size textarea height
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const line = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const minH = 40;
    const next = Math.min(Math.max(el.scrollHeight, minH), 120);
    el.style.height = next + "px";
  }, [draft]);

  // Scroll to bottom when messages change or while streaming
  useEffect(() => { scrollToBottom(); }, [messages, streamingId]);

  const submitUser = async (text) => {
    if (!text || !text.trim()) return;
    const userText = text.trim();

    // store in history
    pushHistory(userText);
    exitBrowseMode();

    setIsSending(true);

    // 1) Add user message
    const userId = addMessage({ role: Role.User, content: userText });

    // 2) Create placeholder assistant message (so it can stream)
    const asstId = addMessage({ role: Role.Assistant, content: "" });
    setStreamingId(asstId);

    // 3) Give host a chance to handle
    if (typeof onSend === "function") {
      try {
        const result = await onSend(userText, api);

        if (result == null) { setStreamingId(null); setIsSending(false); return; }

        if (typeof result === "string") {
          updateMessage(asstId, { content: result });
          setStreamingId(null); setIsSending(false);
          return;
        }

        if (result && typeof result === "object") {      
          if (typeof result?.content === "string") {
            updateMessage(asstId, result);
            setStreamingId(null); setIsSending(false);
            return;
          }

          if (result.stream && Symbol.asyncIterator in result.stream) {
            for await (const chunk of result.stream) {
              appendToMessage(asstId, String(chunk ?? ""));
            }
            setStreamingId(null); setIsSending(false);
            return;
          }
        }

        setStreamingId(null); setIsSending(false);
      } catch (err) {
        const debugErrorMessage = String(err?.stack || err?.message || err || "").trim();
        updateMessage(asstId, {
          role: Role.Error,
          content: "Error while connecting to server, please try again.",
          debugErrorMessage: debugErrorMessage || undefined
        });
        setStreamingId(null); setIsSending(false);
      }
    } else {
      setStreamingId(null); setIsSending(false);
    }
  };
  submitMessageRef.current = submitUser;

  // Build stable API
  const api = useMemo(() => createStableApi({
    getMessagesRef,
    setMessages,
    addMessage,
    updateMessage,
    appendToMessage,
    clear,
    focusInput,
    scrollToBottom,
    submitMessage: (text) => submitMessageRef.current(text),
  }), []);

  // Expose API via ref and call onInit once
  useImperativeHandle(ref, () => api, [api]);
  useEffect(() => { __ensureAIChatStyles(); if (typeof onInit === "function") onInit(api); /* eslint-disable-next-line */ }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    if (disabled || isSending) return;
    const text = draft;
    setDraft("");
    submitUser(text);
  };


  const noMeta = (e) => !(e.shiftKey || e.altKey || e.ctrlKey || e.metaKey);

  const onKeyDown = (e) => {
    // Submit on Enter (no Shift)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && !isSending && draft.trim()) {
        const text = draft;
        setDraft("");
        submitUser(text);
      }
      return;
    }

    // History navigation: always intercept ArrowUp/ArrowDown
    if (e.key === "ArrowUp" && noMeta(e)) {
      e.preventDefault();
      browseHistory(-1); // previous
      return;
    }
    if (e.key === "ArrowDown" && noMeta(e)) {
      e.preventDefault();
      browseHistory(+1); // next
      return;
    }
  };

  const onChangeDraft = (e) => {
    const val = e.target.value;
    setDraft(val);
    // If user types, exit browse mode so arrows act on current text again
    if (histIndexRef.current !== -1) exitBrowseMode();
  };

  const resolveAssociatedOutputTarget = (message) => {
    if (typeof getAssociatedOutputTarget !== "function") return null;
    return getAssociatedOutputTarget(message) || null;
  };

  const openAssociatedOutput = (message, target) => {
    if (!target || typeof onOpenAssociatedOutput !== "function") return;
    onOpenAssociatedOutput(message, target);
  };

  const isErrorTurnMessage = (message) =>
    Boolean(message?.isErrorTurn) || String(message?.role ?? "").toLowerCase() === Role.Error;

  const openMessageDetails = (message) => {
    if (typeof onOpenMessageDetails === "function") {
      onOpenMessageDetails(message);
      return;
    }

    if (message?._id && typeof onOpenDetails === "function") {
      onOpenDetails(message._id);
    }
  };

  return (
    <div className={`ai-chat ${className}`} style={{ height: '100%' }}>
      {/* Messages area */}
      <div className="ai-scroll" ref={listRef}>
        {scrollContent ? (
          <div className="ai-host-scroll-content">
            {scrollContent}
          </div>
        ) : (
          <div className="ai-messages">
            {messages.length === 0 && (isSending || streamingId) ? (
              <div className="flex items-center justify-center py-12 gap-2">
                <span className="ai-spinner ai-lg" />
                <span>Preparing…</span>
              </div>
            ) : null}
            {messages.map(m => {
              const isErrorTurn = isErrorTurnMessage(m);
              const outputTarget = !isErrorTurn && m.role === Role.Assistant && m.content
                ? resolveAssociatedOutputTarget(m)
                : null;
              return (
                (m.role === Role.Assistant || isErrorTurn) && m.content ? (
                  <div key={m.id} className="ai-row">
                    <Bubble
                      id={m.id}
                      role={m.role}
                      onClick={() => {
                        if (isErrorTurn) {
                          openMessageDetails(m);
                          return;
                        }
                        openAssociatedOutput(m, outputTarget);
                      }}
                      title={isErrorTurn ? "Open error details" : outputTarget?.title}
                    >
                      {!m.content ? "..." : m.content}
                    </Bubble>
                    <div className="flex-1" />
                    {outputTarget ? (
                      <button
                        type="button"
                        className="ai-details-info ai-associated-output"
                        onClick={(event) => {
                          event.stopPropagation();
                          openAssociatedOutput(m, outputTarget);
                        }}
                        aria-label={outputTarget?.title || "View associated output"}
                        title={outputTarget?.title || "View associated output"}
                      >
                        <IconControl iconKey={outputTarget?.iconKey || "analytics"} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="ai-details-info"
                      onClick={(event) => {
                        event.stopPropagation();
                        openMessageDetails(m);
                      }}
                      aria-label="View details"
                      title="View details"
                    >
                      ⓘ
                    </button>
                  </div>
                ) : m.content && (
                  <Bubble key={m.id} id={m.id} role={m.role}>{!m.content ? "..." : m.content}</Bubble>
                )
              );
            })}
            {/* streaming indicator bubble */}
            <div ref={anchorRef} aria-hidden className="h-px w-full" />
          </div>
        )}
      </div>

      {(isSending || streamingId) && (
        <ThinkingBar aiChatId={streamingId} onOpenDetails={onOpenDetails} thinking={thinking} />
      )}

      {reviewFooterContent ? (
        <div className="ai-chat-review-footer">
          {reviewFooterContent}
        </div>
      ) : null}

      {/* Input bar */}
      <InputBar
        textareaRef={textareaRef}
        value={draft}
        onChange={onChangeDraft}
        onSubmit={onSubmit}
        onKeyDown={onKeyDown}
        disabled={disabled}
        submitDisabled={disabled || isSending}
        placeholder={placeholder}
        submitLabel={sendButtonLabel}
      />
      {footerContent ? <div className="ai-chat-extra-footer">{footerContent}</div> : null}
    </div>
  );
};

const AIChatControl = forwardRef(AIChatControlInner);
export default AIChatControl;
